import { useState, useCallback } from 'react';
import { Form, Modal } from 'react-aria-components';
import { SubmitHandler, useForm } from 'react-hook-form';

import { confirmSignIn, signIn, fetchAuthSession } from 'aws-amplify/auth';
import { useAtom } from 'jotai';

import { getUserByEmail } from '@/api/getUserByEmail';
import Button from '@/components/Button/Button';
import InputText from '@/components/InputText/InputText';
import NotificationBanner from '@/components/NotificationBanner/NotificationBanner';
import { NotificationAtom, UserAtom } from '@/jotai/jotai';

type LoginInput = {
  email: string;
  password: string;
};

type UpdatePassword = {
  password: string;
};

export const LoginPage = () => {
  const [_, setNotification] = useAtom(NotificationAtom);
  const [isOpen, setIsOpen] = useState(false);
  const { register, handleSubmit } = useForm<LoginInput>();
  const { register: register2, handleSubmit: handleSubmit2 } = useForm<UpdatePassword>();
  const [, setUser] = useAtom(UserAtom);

  const onSubmit: SubmitHandler<LoginInput> = async (data) => {
    try {
      const result = await signIn({
        username: data.email,
        password: data.password,
      });
      if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setIsOpen(true);
      }
      // TODO: token周りは後で修正
      const session = await fetchAuthSession();
      const token = session?.tokens?.idToken?.toString();
      if (token) {
        localStorage.setItem('token', token);
        localStorage.setItem('email', data.email);
        fetchUserByEmail(data.email);
      }
      setNotification({
        headingLevel: 'h2',
        type: 'success',
        bannerStyle: 'standard',
        title: 'ログインに成功しました',
        message: 'ログインに成功しました',
      });
    } catch (e) {
      setNotification({
        headingLevel: 'h2',
        type: 'error',
        bannerStyle: 'standard',
        title: 'ログインに失敗しました',
        message: 'ログインに失敗しました',
      });
    }
  };

  const fetchUserByEmail = useCallback(async (email: string) => {
    try {
      const result = await getUserByEmail(email);
      if (result.status === 200) {
        setUser(result.result);
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  const onSubmitUpdatePassword: SubmitHandler<UpdatePassword> = async (data) => {
    try {
      const result2 = await confirmSignIn({
        challengeResponse: data.password,
      });
      if (result2.isSignedIn) {
        setNotification({
          headingLevel: 'h2',
          type: 'success',
          bannerStyle: 'standard',
          title: 'ログインに成功しました',
          message: 'ログインに成功しました',
        });
      }
    } catch (e) {
      setNotification({
        headingLevel: 'h2',
        type: 'error',
        bannerStyle: 'standard',
        title: 'ログインに失敗しました',
        message: 'ログインに失敗しました',
      });
    }
  };

  return (
    <>
      <div className="md:mt-20">
        <NotificationBanner className="mx-auto w-1/2" />
        <h1 className="my-10 text-center text-2xl font-700">放デイシステム（仮）にログイン</h1>
        <div className="m-auto flex max-w-[800px] flex-col items-center justify-center rounded-3xl bg-white outline-solid-gray-420 md:w-full">
          <div className="space-y-8 md:px-8 md:py-14">
            <h2 className="mb-6 text-center text-2xl font-bold">ログイン</h2>
            <Form
              className="md:w-[400px]"
              onSubmit={handleSubmit(onSubmit)}
            >
              <InputText
                name="email"
                type="email"
                label="メールアドレス"
                required
                register={register('email', { required: '必須' })}
              />
              <InputText
                name="password"
                type="password"
                label="パスワード"
                supportText="英数字記号10文字以上"
                required
                register={register('password', { required: '必須' })}
              />
              <div className="mt-8">
                <Button
                  className="w-full"
                  variant="solid-fill"
                  size="lg"
                  type="submit"
                >
                  ログイン
                </Button>
              </div>
            </Form>
            <div className="text-center">
              <a
                className="text-blue-800 underline"
                href="/admin/password-reminder"
              >
                パスワードをお忘れの方はこちら
              </a>
            </div>
          </div>
        </div>
      </div>
      <Modal
        isOpen={isOpen}
        className="fixed right-0 top-0 z-50 flex h-full w-full items-center justify-center bg-black/50"
      >
        <div className="m-auto flex min-h-[550px] max-w-[800px] flex-col items-center justify-center rounded-3xl bg-white outline outline-1 md:w-full">
          <div className="space-y-8 p-8">
            <h2 className="mb-6 text-center text-4xl">パスワードの更新</h2>
            <Form onSubmit={handleSubmit2(onSubmitUpdatePassword)}>
              <InputText
                name="password"
                type="password"
                label="パスワード"
                supportText="英数字10文字以上"
                required
                register={register2('password', { required: '必須' })}
              />
              <div className="mt-8">
                <Button
                  size="lg"
                  variant="solid-fill"
                  type="submit"
                >
                  更新
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default LoginPage;
